import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { cancelBookingByLink } from "@/lib/agenda.functions";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { buildHead } from "@/lib/seo-head";

interface CancelSearch {
  id: string | undefined;
  t: string | undefined;
}

export const Route = createFileRoute("/agenda/cancelar")({
  validateSearch: (search: Record<string, unknown>): CancelSearch => ({
    id: typeof search["id"] === "string" ? search["id"] : undefined,
    t: typeof search["t"] === "string" ? search["t"] : undefined,
  }),
  head: () =>
    buildHead({
      title: "Cancelar reunião — OP2P",
      description: "Confirme o cancelamento da sua reunião com a OP2P e libere o horário na agenda.",
      path: "/agenda/cancelar",
    }),
  component: CancelPage,
});

function CancelPage() {
  const { id, t } = Route.useSearch();
  const cancel = useServerFn(cancelBookingByLink);
  const [reason, setReason] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [message, setMessage] = useState("");

  const invalid = !id || !t;

  async function handleCancel() {
    if (invalid) return;
    setState("loading");
    try {
      await cancel({ data: { id: id!, token: t!, reason } });
      setState("done");
    } catch (error) {
      setState("error");
      setMessage(error instanceof Error ? error.message : "Não foi possível cancelar.");
    }
  }

  return (
    <main className="px-6 pt-32 pb-12">
      <div className="mx-auto w-full max-w-lg rounded-3xl border border-border bg-card p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-foreground">Cancelar reunião</h1>

        {invalid && (
          <p className="mt-4 text-muted-foreground">
            Este link de cancelamento está incompleto. Use o botão do e-mail de confirmação.
          </p>
        )}

        {!invalid && state !== "done" && (
          <>
            <p className="mt-3 text-muted-foreground">
              Ao confirmar, o horário volta a ficar livre na agenda, o convite sai do calendário e todos recebem
              o aviso por e-mail.
            </p>
            <Textarea
              className="mt-5"
              placeholder="Motivo (opcional)"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            {state === "error" && <p className="mt-3 text-sm text-destructive">{message}</p>}
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={handleCancel} disabled={state === "loading"} variant="destructive">
                {state === "loading" ? "Cancelando…" : "Confirmar cancelamento"}
              </Button>
              <Button asChild variant="outline">
                <Link to="/agenda">Escolher outra data</Link>
              </Button>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Quer manter a reunião?{" "}
              <Link to="/agenda" className="underline underline-offset-4 hover:text-foreground">
                Voltar para a agenda
              </Link>
            </p>
          </>
        )}

        {state === "done" && (
          <>
            <p className="mt-4 text-muted-foreground">
              Reunião cancelada. O horário já está liberado na agenda e o aviso foi enviado por e-mail.
            </p>
            <Button asChild className="mt-6">
              <Link to="/agenda">Escolher uma nova data</Link>
            </Button>
          </>
        )}
      </div>
    </main>
  );
}
